import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import "./AddressBlock.scss";
import { useIntl } from "react-intl";
import INewAddress from "../../../models/INewAddress";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import { sortBy, isNil, partition } from "lodash-es";
import countriesWithLang, { getStateProvinceData, getStateProvinceLabel, separatorItem } from "../../../services/CountriesConstService";
import IAddress from "../../../models/IAddress";
import OwLabel from "../OwLabel/OwLabel";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import SupplierContext from "../../../contexts/SupplierContext";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwButton from "../OwButton/OwButton";
import { SocketContext } from "../../../contexts/SocketContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IPostGridOnVerifyAddressDTO from "../../../models/PostGrid/IPostGridOnVerifyAddressDTO";
import IPostGridVerifySingleAddress from "../../../models/PostGrid/IPostGridVerifySingleAddress";
import IPostGridVerifyAddressDTO from "../../../models/PostGrid/IPostGridVerifyAddressDTO";
import UserContext from "../../../contexts/UserContext";
import { AddressVerificationErrorDisplay } from "./AddressVerificationErrorDisplay/AddressVerificationErrorDisplay";
import Modal from "react-modal";
import { AddressCorrectionModal } from "./AddressCorrectionModal/AddressCorrectionModal";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import { v4 } from "uuid";
import UiContext from "@contexts/UiContext";
import ICountryObject from "@models/ICountryObject";

type Props = {
    address?: IAddress | INewAddress;
    countries?: any[];
    prefix?: string;
    fn: any;
    showInternalId?: boolean;
    showName?: boolean;
    showCompanyName?: boolean;
    companyNameRequired?: boolean;
    isDisabled?: boolean;
    requiredFields?: boolean;
    inputClassName?: string;
    className?: string;
    showPhoneFax?: boolean;
    showEmail?: boolean;
    maxNameLength?: number;
    maxCompanyNameLength?: number;
    hideFax?: boolean;
    updateEntireAddressFn?: (address: IAddress | INewAddress) => void;
};

export const AddressBlock: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext);
    const uiContext = useContext(UiContext);
    const intl = useIntl();

    const [countries, setCountries] = useState<any[]>(countriesWithLang.countries as any[]);
    const [prefix, setPrefix] = useState(props.prefix || "");
    const [address, setAddress] = useState<IAddress>((props.address || {}) as IAddress);
    const [isCompanyNameRequired, setIsCompanyNameRequired] = useState(false);
    const [countrySelectOption, setCountrySelectOption] = useState<any>(null);
    const [verifyActive, setVerifyActive] = useState(false);
    const [verifyData, setVerifyData] = useState<IPostGridVerifySingleAddress>();
    const [hasVerifyErrors, setHasVerifyErrors] = useState(false);
    const [hasVerifiedAddress, setHasVerifiedAddress] = useState(false);
    const [blockUuid, setBlockUuid] = useState(v4());
    const [stateProvinceLabel, setStateProvinceLabel] = useState("state");
    const [stateProvinceOptions, setStateProvinceOptions] = useState<any[]>([]);

    useEffect(() => {
        setAddress((props.address || {}) as IAddress);
    }, [props.address]);

    const useOriginalAddress = () => {
        const _address = {
            ...address,
        };

        _address.verified = true;

        setHasVerifiedAddress(true);

        uiContext.showModal(false);
        uiContext.setModalComponent(null);

        if (props.updateEntireAddressFn) {
            props.updateEntireAddressFn(_address);
        }
    };

    const useCorrectedAddress = (data: IPostGridVerifySingleAddress) => {
        const _address = {
            ...address,
        };

        _address.address_line_one = data.data.line1;
        _address.address_line_two = "";
        _address.address_line_three = "";
        _address.city = data.data.city;
        _address.state = data.data.provinceOrState;
        _address.postal_code = data.data.postalOrZip;

        _address.verified_data = data;
        _address.verified = true;

        setAddress(_address);
        setHasVerifiedAddress(true);

        uiContext.showModal(false);
        uiContext.setModalComponent(null);

        if (props.updateEntireAddressFn) {
            props.updateEntireAddressFn(_address);
        }
    };

    const verifyAddress = (_address: INewAddress) => {
        const payload: IPostGridVerifyAddressDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            address: _address,
            blockUuid,
        };

        socket?.emit(EndpointsEnum.AddressVerifyAddress, payload);

        setVerifyActive(true);
    };

    const onVerifyAddress = (data: IPostGridVerifySingleAddress) => {
        if (data.blockUuid === blockUuid) {
            setVerifyActive(false);
            setVerifyData(data);

            if (data.data.status === "corrected") {
                // show the correction modal.
                const modalComponent = (
                    <AddressCorrectionModal
                        initialAddress={address}
                        data={data}
                        useCorrectedAddressFn={useCorrectedAddress}
                        useOriginalAddressFn={useOriginalAddress}
                    />
                );

                uiContext.setModalComponent(modalComponent);
                uiContext.showModal(true);

                setHasVerifyErrors(false);
            } else if (data.data.status === "verified") {
                const _address = {
                    ...address,
                };

                _address.verified = true;
                _address.verified_data = data;

                setAddress(_address);
                setHasVerifyErrors(false);
                setHasVerifiedAddress(true);
            } else {
                setHasVerifyErrors(!!Object.keys(data.data.errors).length);
            }
        }
    };

    const notify = (data: IErrorDTO) => {
        setVerifyActive(false);
    };

    const sockets: IUiSocketSetup[] = [];

    if (supplierModel.has_address_validation) {
        sockets.push({
            endpoint: EndpointsEnum.AddressOnVerifyAddress,
            fn: onVerifyAddress,
        });

        sockets.push({
            endpoint: EndpointsEnum.Notify,
            fn: notify,
        });
    }

    useEffect(() => {
        for (const _socket of sockets) {
            socket?.on(_socket.endpoint, _socket.fn);
        }

        const _countrySelectOption = getCountrySelectOption();
        setCountrySelectOption(_countrySelectOption);

        if (props.requiredFields) {
            if (!isNil(props.companyNameRequired)) {
                setIsCompanyNameRequired(props.companyNameRequired);
            }
        }

        // Set initial state/province data based on country
        const initialCountry = props.address?.country || "USA";
        updateStateProvinceForCountry(initialCountry);

        return () => {
            for (const _socket of sockets) {
                socket?.removeListener(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const getCountrySelectOption = () => {
        // Define priority countries that should appear at the top
        const priorityCountries = ["United States", "Canada", "Mexico", "Australia", "United Kingdom"];

        const sortedCountries = sortBy(countries, (country) => {
            return country.name.common;
        });

        const partitionedCountries = partition(sortedCountries, (country) => {
            return priorityCountries.includes(country.name.common);
        });

        const priorityCountriesSorted = partitionedCountries[0];
        const otherCountries = partitionedCountries[1];

        // Sort priority countries in the order specified in priorityCountries array
        const orderedPriorityCountries = priorityCountries
            .map((priorityName) => priorityCountriesSorted.find((country) => country.name.common === priorityName))
            .filter((country): country is NonNullable<typeof country> => country !== undefined);

        const combinedCountries: any[] = orderedPriorityCountries.concat([separatorItem]).concat(otherCountries);
        setCountries(combinedCountries);

        const defaultValue = "USA"; // props.address.country

        if (!props.address) {
            return null;
        }

        const selectOptionFormattedCountries = combinedCountries.map((country, index) => {
            let languageCode: any = "eng";

            if (languageCode === "eng") {
                languageCode = undefined;
            }

            let detail = country;
            let common_name = detail.name.common;
            const cca3 = detail.cca3;

            // Handle separator specially
            if (common_name === "---") {
                return {
                    detail,
                    common_name: "──────────",
                    cca3: "SEPARATOR",
                    disabled: true,
                };
            }

            if (languageCode) {
                detail = country.translations[languageCode];
                common_name = detail.common;
            }

            return {
                detail,
                common_name,
                cca3,
            };
        });

        let prefix = "";

        if (props.prefix) {
            prefix = props.prefix;
        }

        let selectedOption = props.address?.country || "";

        if (selectedOption === "") {
            selectedOption = defaultValue;
        }

        if (supplierModel.freetext_country) {
            return (
                <OwFnInput
                    testId={"country"}
                    fn={handleChange}
                    name={prefix + "country"}
                    value={address.country || ""}
                    disabled={props.isDisabled}
                    className={props.inputClassName + " mb-1 form-control-sm"}
                    placeholder={"country"}
                    isRequired={props.requiredFields}
                />
            );
        } else {
            return (
                <OwSelectOption
                    testId={"country"}
                    titleProp={"common_name"}
                    valueProp={"cca3"}
                    isDisabled={props.isDisabled}
                    fn={handleChange}
                    options={selectOptionFormattedCountries}
                    name={prefix + "country"}
                    selectedOption={selectedOption}
                    showBlankFirst={true}
                    hasSelectedOptionProp={false}
                />
            );
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        // Prevent selection of separator
        if (e.target.value === "SEPARATOR") {
            return;
        }

        // If country changed, update state/province label and options
        if (e.target.name === prefix + "country") {
            const countryCode = e.target.value;
            updateStateProvinceForCountry(countryCode);
        }

        props.fn(e);
    };

    const updateStateProvinceForCountry = (countryCode: string) => {
        const label = getStateProvinceLabel(countryCode);
        setStateProvinceLabel(label);

        const options = getStateProvinceData(countryCode);
        setStateProvinceOptions(options);
    };

    return (
        <div className={"AddressBlock " + (props.className || "")}>
            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"generic"} /> : null}

            {props.showInternalId ? (
                <div>
                    <OwFnInput
                        testId={"address_internal_id"}
                        disabled={props.isDisabled}
                        name={prefix + "address_internal_id"}
                        className={props.inputClassName + " mb-1 form-control-sm"}
                        placeholder={"address_internal_id"}
                        fn={handleChange}
                        value={address.address_internal_id || ""}
                        isRequired={props.requiredFields}
                    />
                </div>
            ) : null}

            {props.showName ? (
                <div>
                    <OwFnInput
                        testId={"name"}
                        disabled={props.isDisabled}
                        name={prefix + "name"}
                        className={props.inputClassName + " mb-1 form-control-sm"}
                        placeholder={"name"}
                        fn={handleChange}
                        value={address.name || ""}
                    />
                </div>
            ) : null}

            {props.showCompanyName ? (
                <div>
                    <OwFnInput
                        testId={"company_name"}
                        disabled={props.isDisabled}
                        name={prefix + "company_name"}
                        className={props.inputClassName + " mb-1 form-control-sm"}
                        placeholder={"company_name"}
                        fn={handleChange}
                        value={address.company_name || ""}
                        isRequired={isCompanyNameRequired}
                    />
                </div>
            ) : null}

            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"line1"} /> : null}

            <OwFnInput
                testId={"address_line_one"}
                disabled={props.isDisabled}
                name={prefix + "address_line_one"}
                className={props.inputClassName + " mb-1 form-control-sm"}
                placeholder={"address_line_one"}
                fn={handleChange}
                value={address.address_line_one || ""}
                isRequired={props.requiredFields}
            />

            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"line2"} /> : null}

            <OwFnInput
                testId={"address_line_two"}
                disabled={props.isDisabled}
                name={prefix + "address_line_two"}
                className={props.inputClassName + " mb-1 form-control-sm"}
                placeholder={"address_line_two"}
                fn={handleChange}
                value={address.address_line_two || ""}
            />

            <OwFnInput
                testId={"address_line_three"}
                disabled={props.isDisabled}
                name={prefix + "address_line_three"}
                className={props.inputClassName + " mb-1 form-control-sm"}
                placeholder={"address_line_three"}
                fn={handleChange}
                value={address.address_line_three || ""}
            />

            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"city"} /> : null}

            <OwFnInput
                testId={"city"}
                disabled={props.isDisabled}
                name={prefix + "city"}
                className={props.inputClassName + " mb-1 form-control-sm"}
                placeholder={"city"}
                fn={handleChange}
                value={address.city || ""}
                isRequired={props.requiredFields}
            />

            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"provinceOrState"} /> : null}

            {stateProvinceOptions.length > 0 ? (
                <div className="mb-1">
                    <OwSelectOption
                        testId={"state"}
                        titleProp={"code"}
                        valueProp={"code"}
                        isDisabled={props.isDisabled}
                        fn={handleChange}
                        options={stateProvinceOptions}
                        name={prefix + "state"}
                        selectedOption={address.state || ""}
                        showBlankFirst={true}
                        hasSelectedOptionProp={false}
                        className={props.inputClassName + " form-control-sm"}
                    />
                </div>
            ) : (
                <OwFnInput
                    testId={"state"}
                    disabled={props.isDisabled}
                    name={prefix + "state"}
                    className={props.inputClassName + " mb-1 form-control-sm"}
                    placeholder={intl.formatMessage({ id: stateProvinceLabel })}
                    fn={handleChange}
                    value={address.state || ""}
                    isRequired={props.requiredFields}
                />
            )}

            {hasVerifyErrors ? <AddressVerificationErrorDisplay data={verifyData} errorProp={"postalOrZip"} /> : null}

            <OwFnInput
                testId={"postal_code"}
                disabled={props.isDisabled}
                name={prefix + "postal_code"}
                className={props.inputClassName + " mb-1 form-control-sm"}
                placeholder={"postal_code"}
                fn={handleChange}
                value={address.postal_code || ""}
                isRequired={props.requiredFields}
            />

            {countrySelectOption}

            {props.showPhoneFax ? (
                <div>
                    <div>
                        <div className={"mt-2"}>
                            <OwLabel title={"phone"} noBottomMargin={true} />
                        </div>

                        <div>
                            <OwFnInput testId={"phone"} name={prefix + "phone"} fn={handleChange} value={address.phone || ""} />
                        </div>
                    </div>

                    {props.hideFax ? null : (
                        <div>
                            <div className={"mt-2"}>
                                <OwLabel title={"fax"} noBottomMargin={true} />
                            </div>

                            <div>
                                <OwFnInput testId={"fax"} name={prefix + "fax"} fn={props.fn} value={address.fax || ""} />
                            </div>
                        </div>
                    )}
                </div>
            ) : null}

            {props.showEmail ? (
                <div>
                    <div>
                        <div className={"mt-2"}>
                            <OwLabel title={"email"} noBottomMargin={true} />
                        </div>

                        <div>
                            <OwFnInput
                                testId={"email"}
                                name={prefix + "email"}
                                fn={props.fn}
                                value={address.email || ""}
                                autoComplete={false}
                            />
                        </div>
                    </div>
                </div>
            ) : null}

            {supplierModel.has_address_validation ? (
                <div>
                    <div className={"mt-2"}>
                        <OwButton
                            title={"verify_address"}
                            fn={() => {
                                verifyAddress(address);
                            }}
                            active={verifyActive}
                            disableOnActive={true}
                        />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default AddressBlock;
